// src/pages/superadmin/SuperAdminPasswords.jsx
import React, { useState } from 'react';
import axios from 'axios';
import {
  Key, Shield, CheckCircle, XCircle, Eye, EyeOff,
  RefreshCw, Save, AlertTriangle, Lock, User
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const SuperAdminPasswords = () => {
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [policy, setPolicy] = useState({
    min_length: 8,
    require_uppercase: true,
    require_lowercase: true,
    require_numbers: true,
    require_special: true,
    expiry_days: 90
  });

  const [policyLoading, setPolicyLoading] = useState(false);
  const [policySaved, setPolicySaved] = useState(false);

  // Password strength check
  const checkStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return Math.min(100, (score / 7) * 100);
  };

  const getStrengthColor = (strength) => {
    if (strength < 30) return 'bg-red-500';
    if (strength < 60) return 'bg-yellow-500';
    if (strength < 80) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStrengthText = (strength) => {
    if (strength < 30) return 'Weak';
    if (strength < 60) return 'Fair';
    if (strength < 80) return 'Good';
    return 'Strong';
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (formData.new_password !== formData.confirm_password) {
      setResult({ type: 'error', message: 'New passwords do not match' });
      return;
    }
    if (formData.new_password.length < 8) {
      setResult({ type: 'error', message: 'Password must be at least 8 characters' });
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      await axios.post(`${API_BASE}/auth/change-password`, {
        current_password: formData.current_password,
        new_password: formData.new_password
      });
      setResult({ type: 'success', message: 'Password changed successfully!' });
      setFormData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setResult({ type: 'error', message: error.response?.data?.detail || 'Error changing password' });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePolicy = async () => {
    setPolicyLoading(true);
    try {
      await axios.put(`${API_BASE}/admin/password-policy`, policy);
      setPolicySaved(true);
      setTimeout(() => setPolicySaved(false), 3000);
    } catch (error) {
      alert('Error saving password policy');
    } finally {
      setPolicyLoading(false);
    }
  };

  const strength = checkStrength(formData.new_password);
  const strengthColor = getStrengthColor(strength);
  const strengthText = getStrengthText(strength);

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Key className="w-5 h-5 text-yellow-400" />
          Password Management
        </h2>
        <p className="text-gray-400 text-sm">Change your password and manage security policies</p>
      </div>

      {/* Change Password Form */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-4 h-4 text-blue-400" />
          <h3 className="text-white font-semibold">Change Password</h3>
        </div>

        {result && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
            result.type === 'success' 
              ? 'bg-green-500/20 border border-green-500/30 text-green-400'
              : 'bg-red-500/20 border border-red-500/30 text-red-400'
          }`}>
            {result.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {result.message}
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Current Password</label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={formData.current_password}
                onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">New Password</label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={formData.new_password}
                onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {formData.new_password && (
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-400">Password strength: {strengthText}</span>
                  <span className="text-gray-400">{Math.round(strength)}%</span>
                </div>
                <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div className={`h-full ${strengthColor} rounded-full transition-all duration-300`} style={{ width: `${strength}%` }} />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Requirements: min 8 chars, uppercase, lowercase, number, special character
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Confirm New Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {formData.confirm_password && formData.new_password !== formData.confirm_password && (
              <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Password Policy Settings (Super Admin only) */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-purple-400" />
            <h3 className="text-white font-semibold">Password Policy</h3>
          </div>
          {policySaved && (
            <span className="text-xs text-green-400 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Saved
            </span>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Minimum Length</label>
            <input
              type="number"
              min="6"
              max="20"
              value={policy.min_length}
              onChange={(e) => setPolicy({ ...policy, min_length: parseInt(e.target.value) })}
              className="w-24 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={policy.require_uppercase}
                onChange={(e) => setPolicy({ ...policy, require_uppercase: e.target.checked })}
                className="w-4 h-4 rounded border-gray-600"
              />
              <span className="text-gray-300 text-sm">Require uppercase letter (A-Z)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={policy.require_lowercase}
                onChange={(e) => setPolicy({ ...policy, require_lowercase: e.target.checked })}
                className="w-4 h-4 rounded border-gray-600"
              />
              <span className="text-gray-300 text-sm">Require lowercase letter (a-z)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={policy.require_numbers}
                onChange={(e) => setPolicy({ ...policy, require_numbers: e.target.checked })}
                className="w-4 h-4 rounded border-gray-600"
              />
              <span className="text-gray-300 text-sm">Require number (0-9)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={policy.require_special}
                onChange={(e) => setPolicy({ ...policy, require_special: e.target.checked })}
                className="w-4 h-4 rounded border-gray-600"
              />
              <span className="text-gray-300 text-sm">Require special character (!@#$%^&*)</span>
            </label>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Password Expiry (days)</label>
            <select
              value={policy.expiry_days}
              onChange={(e) => setPolicy({ ...policy, expiry_days: parseInt(e.target.value) })}
              className="w-32 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
            >
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
              <option value="180">180 days</option>
              <option value="365">365 days</option>
            </select>
          </div>

          <button
            onClick={handleSavePolicy}
            disabled={policyLoading}
            className="mt-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-white text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {policyLoading ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-3 h-3" />}
            Save Policy
          </button>
        </div>
      </div>

      {/* Security Tips */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items gap-2 mb-2">
          <Shield className="w-4 h-4 text-blue-400" />
          <span className="text-blue-400 text-sm font-medium">Security Tips</span>
        </div>
        <ul className="text-xs text-gray-400 space-y-1 ml-6 list-disc">
          <li>Use a unique password that you don't use elsewhere</li>
          <li>Enable two-factor authentication for additional security</li>
          <li>Never share your password with anyone</li>
          <li>Change your password regularly</li>
          <li>If you suspect unauthorized access, change your password immediately</li>
        </ul>
      </div>
    </div>
  );
};

export default SuperAdminPasswords;